import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, BoxProps, Loader, Text } from '@mantine/core';
import { useIntersection } from '@mantine/hooks';

import { useGetBoardTemplatesInfinite } from 'api/templates/hooks';
import { TextInput } from 'components/shared';
import { SearchIcon } from 'icons';

import { RadioBox } from './RadioBox';

export type SelectTemplateProps = BoxProps & {
  value?: string;
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    templateId: string,
  ) => void;
};

export const SelectTemplate: React.FC<SelectTemplateProps> = ({
  value,
  onChange,
  sx,
  ...boxProps
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState<string>();
  const { data, isLoading, isFetchingNextPage, fetchNextPage } =
    useGetBoardTemplatesInfinite({
      queryParams: {
        name: search,
      },
    });

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const templates = data?.pages.map(({ data }) => data)?.flat() || [];

  const { ref: intersectionRef, entry } = useIntersection({
    root: containerRef.current,
    threshold: 1,
  });

  useEffect(() => {
    if (entry?.isIntersecting) {
      fetchNextPage();
    }
  }, [entry, fetchNextPage]);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...sx,
      }}
      {...boxProps}
    >
      <TextInput
        icon={<SearchIcon />}
        placeholder={t('common:search')}
        onChange={e => setSearch(e.target.value)}
      />
      <Box
        ref={containerRef}
        sx={{
          overflow: 'auto',
          height: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            marginBottom: 16,
          }}
        >
          {templates.map((template: any, index: number) => (
            <Box
              ref={index === templates.length - 4 ? intersectionRef : null}
              key={template.id}
            >
              <RadioBox
                name="templateId"
                value={template.id}
                label={<Text>{template.name}</Text>}
                checked={value === template.id}
                onChange={e => onChange?.(e, template.id)}
              />
            </Box>
          ))}

          {(isLoading || isFetchingNextPage) && (
            <Box
              sx={{
                height: 64,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Loader />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
